import React from 'react';
import { Button, Dropdown, Space } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeOutlined, 
  UserOutlined, 
  BookOutlined, 
  SolutionOutlined, 
  ContactsOutlined, 
  LoginOutlined,
  FileTextOutlined,
  StarOutlined,
  InfoCircleOutlined,
  DownOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  FlagOutlined,
  IdcardOutlined,
  HeartOutlined,
  ReadOutlined,
  TrophyOutlined,
  PictureOutlined,
  CalendarOutlined,
  EditOutlined,
  NotificationOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useDynamicMenu, MenuItem, MenuCategory } from '../../hooks/useDynamicMenu';

const iconMap: Record<string, React.ReactNode> = {
  'HomeOutlined': <HomeOutlined />,
  'UserOutlined': <UserOutlined />,
  'BookOutlined': <BookOutlined />,
  'SolutionOutlined': <SolutionOutlined />,
  'ContactsOutlined': <ContactsOutlined />,
  'LoginOutlined': <LoginOutlined />,
  'FileTextOutlined': <FileTextOutlined />,
  'StarOutlined': <StarOutlined />,
  'InfoCircleOutlined': <InfoCircleOutlined />,
  'TeamOutlined': <TeamOutlined />,
  'ClockCircleOutlined': <ClockCircleOutlined />,
  'FlagOutlined': <FlagOutlined />,
  'IdcardOutlined': <IdcardOutlined />,
  'HeartOutlined': <HeartOutlined />,
  'ReadOutlined': <ReadOutlined />,
  'TrophyOutlined': <TrophyOutlined />,
  'PictureOutlined': <PictureOutlined />,
  'CalendarOutlined': <CalendarOutlined />,
  'EditOutlined': <EditOutlined />,
  'NotificationOutlined': <NotificationOutlined />,
  'EnvironmentOutlined': <EnvironmentOutlined />,
  'QuestionCircleOutlined': <QuestionCircleOutlined />,
  'CustomerServiceOutlined': <CustomerServiceOutlined />,
  'AppstoreOutlined': <AppstoreOutlined />
};

const DynamicMenu: React.FC = () => {
  const { navigationItems, menuCategories, loginItem } = useDynamicMenu();
  const location = useLocation();

  const renderNavigationItem = (item: MenuItem, index: number) => {
    const isActive = location.pathname === item.href;
    const icon = item.icon ? iconMap[item.icon] : undefined;
    
    if (item.href) {
      return (
        <Link to={item.href} key={index}>
          <Button 
            type={item.type || "link"}
            className={`nav-item ${isActive ? 'active' : ''}`}
            icon={icon}
          >
            {item.label}
          </Button>
        </Link>
      );
    }
    
    return (
      <Button 
        key={index}
        type={item.type || "link"}
        className="nav-item"
        icon={icon}
      >
        {item.label}
      </Button>
    );
  };

  const renderDropdownMenu = (category: MenuCategory) => {
    const menuItems = category.children.map(child => ({
      key: child.key,
      label: (
        <Link to={child.href || '#'}>
          <Space>
            {child.icon && iconMap[child.icon]}
            {child.label}
          </Space>
        </Link>
      )
    }));

    return {
      items: menuItems,
      key: category.key
    };
  };

  const renderCategoryDropdown = (category: MenuCategory) => {
    const icon = iconMap[category.icon];
    const isActive = category.children.some(child => location.pathname === child.href);
    
    return (
      <Dropdown
        key={category.key}
        menu={renderDropdownMenu(category)}
        placement="bottomLeft"
        trigger={['hover']}
      >
        <Button 
          type="link"
          className={`nav-item dropdown-item ${isActive ? 'active' : ''}`}
          icon={icon}
        >
          <Space>
            {category.label}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    );
  };

  return (
    <Space size="large" className="nav-items">
      {/* Core navigation items */}
      {navigationItems.map((item, index) => renderNavigationItem(item, index))}
      
      {/* Category dropdowns */}
      {menuCategories.map(category => renderCategoryDropdown(category))}
      
      {/* Login button at the end */}
      {renderNavigationItem(loginItem, -1)}
    </Space>
  );
};

export default DynamicMenu;
